import { Fragment, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Divider, Empty, Space, Spin, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FileSearchOutlined } from '@ant-design/icons';

import {
	KeyenceValidationSeverity,
	KeyenceValidationSummary,
} from '../../../../interfaces/Quality.Interface';
import { useGetKeyenceValidationReportQuery } from '../../../../redux/api/quality';
import {
	OUTCOME_COLOR,
	SEVERITY_COLOR,
	findingCodeLabel,
} from '../../../../pages/KeyenceValidationReport/constants';
import ReportDrawer from '../../../../pages/KeyenceValidationReport/ReportDrawer';

const { Text } = Typography;

const PENDING_POLL_MS = 4000;

const SEVERITY_ORDER: Record<KeyenceValidationSeverity, number> = {
	Error: 0,
	Warning: 1,
	Info: 2,
};

export interface UploadValidationResult {
	key: string;
	fileName: string;
	view?: string | null;
	deviceName?: string | null;
	validation: KeyenceValidationSummary | null;
}

type Finding = KeyenceValidationSummary['findings'][number];

const findingSubject = (finding: Finding) => {
	if (finding.balloon_number !== null) return String(finding.balloon_number);
	if (finding.program_label !== null) {
		return finding.program_label ? `"${finding.program_label}"` : '(unnamed)';
	}
	return null;
};

const dash = <Text type="secondary">—</Text>;

const FindingsTable = ({ findings }: { findings: Finding[] }) => {
	const active = useMemo(
		() =>
			findings
				.filter((f) => !f.is_false_positive)
				.sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity])
				.map((f, index) => ({ ...f, key: index })),
		[findings]
	);

	if (active.length === 0) {
		return <Text type="success">Every Keyence balloon for this view matched the plan.</Text>;
	}

	// Same rule as the report drawer: when a misnamed measurement was paired
	// with a missing balloon, both messages already say so.
	const linkedLabels = new Set(
		active
			.map((f) => f.suggested_program_label)
			.filter((label): label is string => label !== null)
	);

	const columns: ColumnsType<Finding & { key: number }> = [
		{
			title: 'Severity',
			dataIndex: 'severity',
			width: 90,
			render: (severity: Finding['severity']) => (
				<Tag color={SEVERITY_COLOR[severity]} className="m-0">
					{severity}
				</Tag>
			),
		},
		{
			title: 'Balloon',
			dataIndex: 'balloon_number',
			width: 80,
			render: (_, row) => findingSubject(row) ?? dash,
		},
		{
			title: 'Issue',
			dataIndex: 'code',
			render: (code: string, row) => (
				<div>
					<Text strong>{findingCodeLabel(code)}</Text>
					<div>
						<Text type="secondary" className="text-xs">
							{row.message}
						</Text>
					</div>
					{row.suggested_balloon_number !== null &&
						!linkedLabels.has(row.program_label ?? '') && (
							<Text type="secondary" className="text-xs">
								Probably meant balloon {row.suggested_balloon_number}
								{row.confidence_band ? ` (${row.confidence_band} confidence)` : ''}
							</Text>
						)}
				</div>
			),
		},
		{
			title: 'Expected',
			dataIndex: 'expected_value',
			width: 95,
			render: (raw: string | null) => raw ?? dash,
		},
		{
			title: 'In program',
			dataIndex: 'actual_value',
			width: 95,
			render: (raw: string | null, row) => {
				if (row.code === 'MISSING_FROM_PROGRAM') {
					return <Text type="danger">Not in program</Text>;
				}
				return raw === null ? dash : <Text type="danger">{raw}</Text>;
			},
		},
	];

	return (
		<Table
			size="small"
			rowKey="key"
			dataSource={active}
			columns={columns}
			pagination={false}
			scroll={{ y: 280 }}
		/>
	);
};

const ResultSection = ({
	result,
	onOpenReport,
}: {
	result: UploadValidationResult;
	onOpenReport: (reportId: string) => void;
}) => {
	const initial = result.validation;
	const reportId = initial?.report_id ?? null;

	// A program too large to parse on the request is checked by a worker; poll
	// its report until the verdict lands so the operator does not have to go
	// looking for it.
	const [settled, setSettled] = useState(initial?.outcome !== 'Pending');
	const { data: live } = useGetKeyenceValidationReportQuery(reportId ?? '', {
		skip: !reportId || settled,
		pollingInterval: PENDING_POLL_MS,
	});
	useEffect(() => {
		if (live && live.outcome !== 'Pending') setSettled(true);
	}, [live]);

	const current =
		initial && live && live.outcome !== 'Pending'
			? {
					...initial,
					outcome: live.outcome,
					error_count: live.error_count,
					warning_count: live.warning_count,
					matched_count: live.matched_count,
					plan_balloon_count: live.plan_balloon_count,
					program_measurement_count: live.program_measurement_count,
					parse_error: live.parse_error,
					findings: (live.findings ?? []).map((f) => ({
						...f,
						confidence: f.confidence === null ? null : Number(f.confidence),
					})),
				}
			: initial;

	const title = (
		<Space wrap size={4}>
			<Text strong style={{ wordBreak: 'break-all' }}>
				{result.fileName}
			</Text>
			{result.view && <Tag>{result.view}</Tag>}
			{result.deviceName && <Tag color="geekblue">{result.deviceName}</Tag>}
		</Space>
	);

	const extra = current && (
		<Tag color={OUTCOME_COLOR[current.outcome]} className="m-0">
			{current.outcome === 'Pending' ? 'Checking…' : current.outcome}
		</Tag>
	);

	let body;
	if (!current) {
		body = (
			<Text type="secondary">
				Not validated: automatic checking is off, or this file is identical to the
				current version.
			</Text>
		);
	} else if (current.outcome === 'Pending') {
		body = (
			<Space>
				<Spin size="small" />
				<Text type="secondary">
					Large program — it is being checked in the background. The result will
					appear here.
				</Text>
			</Space>
		);
	} else if (current.outcome === 'Skipped' || current.outcome === 'Parse Error') {
		body = (
			<Alert
				type={current.outcome === 'Parse Error' ? 'error' : 'info'}
				showIcon
				message={
					current.outcome === 'Parse Error'
						? 'The file could not be read'
						: 'This program was not validated'
				}
				description={current.parse_error}
			/>
		);
	} else {
		body = (
			<div className="flex flex-col gap-3">
				<Space wrap size={[16, 4]}>
					<Text>
						<Text strong>{current.matched_count}</Text> of{' '}
						<Text strong>{current.plan_balloon_count}</Text> plan balloons matched
					</Text>
					<Text type="secondary">
						{current.program_measurement_count} measurement(s) in the program
					</Text>
					{current.error_count > 0 && (
						<Tag color={SEVERITY_COLOR.Error}>{current.error_count} error(s)</Tag>
					)}
					{current.warning_count > 0 && (
						<Tag color={SEVERITY_COLOR.Warning}>
							{current.warning_count} warning(s)
						</Tag>
					)}
				</Space>
				<FindingsTable findings={current.findings} />
			</div>
		);
	}

	return (
		<section className="flex flex-col gap-3">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<Space wrap size={4}>
					{title}
					{extra}
				</Space>
				{reportId && (
					<Button
						type="link"
						size="small"
						className="p-0"
						icon={<FileSearchOutlined />}
						onClick={() => onOpenReport(reportId)}
					>
						View full report
					</Button>
				)}
			</div>
			{body}
		</section>
	);
};

const UploadValidationResults = ({
	results,
	refused = false,
	showUploadSummary = true,
}: {
	results: UploadValidationResult[];
	refused?: boolean;
	/** The banner speaks about the upload just made; off when reviewing later. */
	showUploadSummary?: boolean;
}) => {
	const [openReportId, setOpenReportId] = useState<string | null>(null);
	const { data: openReport } = useGetKeyenceValidationReportQuery(openReportId ?? '', {
		skip: !openReportId,
	});

	const failed = results.filter((r) => r.validation?.outcome === 'Failed').length;
	const warned = results.filter((r) => r.validation?.outcome === 'Warning').length;

	let summary;
	if (refused) {
		summary = (
			<Alert
				type="error"
				showIcon
				message="Upload refused — nothing was saved or sent to the Keyence"
				description="At least one program does not match the inspection plan. Fix the program(s) below and upload again."
			/>
		);
	} else if (failed > 0) {
		summary = (
			<Alert
				type="warning"
				showIcon
				message={`Uploaded, but ${failed} program(s) do not match the inspection plan`}
				description="Review the issues below before running the program(s)."
			/>
		);
	} else if (warned > 0) {
		summary = (
			<Alert
				type="warning"
				showIcon
				message={`Uploaded with warnings on ${warned} program(s)`}
				description="Nothing blocking was found, but review the warnings below before running the program(s)."
			/>
		);
	} else if (results.some((r) => r.validation?.outcome === 'Passed')) {
		summary = (
			<Alert type="success" showIcon message="Uploaded — the program(s) match the inspection plan" />
		);
	}

	return (
		<div className="flex flex-col gap-3">
			{showUploadSummary && summary}
			{results.length === 0 ? (
				<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No programs were uploaded" />
			) : (
				results.map((result, index) => (
					<Fragment key={result.key}>
						{index > 0 && <Divider className="my-1" />}
						<ResultSection result={result} onOpenReport={setOpenReportId} />
					</Fragment>
				))
			)}
			<ReportDrawer
				report={openReportId && openReport ? openReport : null}
				onClose={() => setOpenReportId(null)}
			/>
		</div>
	);
};

export default UploadValidationResults;
